import { useEffect, useState, useCallback, useMemo } from 'react';
import { Switch } from '../../../../components/ui/switch';
import { connectorsApi } from '../../../../services/connectorsApi';

export interface ConnectorTool {
  name: string;
  kind: string;
  approval_scope?: string;
}

interface ConnectorApprovalPreferencesProps {
  workspaceId: string;
  provider: string;
  tools: ConnectorTool[];
}

// Human-readable labels for approval scopes — the raw tool names
// (propose_send_email, execute_send_email, …) are agent-facing, not user-facing.
const SCOPE_LABELS: Record<string, string> = {
  send_email: 'Before sending emails',
  draft_email: 'Before saving email drafts',
  create_event: 'Before creating calendar events',
  update_event: 'Before updating calendar events',
  cancel_event: 'Before cancelling calendar events',
  create_page: 'Before creating Notion pages',
  update_page: 'Before updating Notion pages',
  create_issue: 'Before creating Linear issues',
  update_issue: 'Before updating Linear issues',
};

function humanLabel(scope: string): string {
  if (SCOPE_LABELS[scope]) return SCOPE_LABELS[scope];
  // Fallback: "send_email" → "Before send email"
  return `Before ${scope.replace(/_/g, ' ')}`;
}

export function ConnectorApprovalPreferences({
  workspaceId,
  provider,
  tools,
}: ConnectorApprovalPreferencesProps) {
  // Dedupe by approval_scope so `propose_send_email` and `execute_send_email`
  // collapse into one user-facing toggle (both control the same pref key).
  const writeScopes = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of tools) {
      if (t.kind !== 'write' || !t.approval_scope) continue;
      if (seen.has(t.approval_scope)) continue;
      seen.add(t.approval_scope);
      out.push(t.approval_scope);
    }
    return out;
  }, [tools]);

  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    connectorsApi
      .getPreferences(workspaceId)
      .then((r: any) => {
        setPrefs((r?.data?.approval_required as Record<string, boolean>) ?? {});
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [workspaceId]);

  const handleToggle = useCallback(
    async (scope: string, newValue: boolean) => {
      // Optimistic update
      setPrefs((prev) => ({ ...prev, [scope]: newValue }));

      try {
        await connectorsApi.updatePreferences(workspaceId, {
          ...prefs,
          [scope]: newValue,
        });
      } catch {
        // Revert on error
        setPrefs((prev) => ({ ...prev, [scope]: !newValue }));
      }
    },
    [workspaceId, prefs],
  );

  if (!writeScopes.length || !loaded) return null;

  return (
    <div className="mt-3 space-y-2 border-t border-border pt-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Require approval
      </p>
      {writeScopes.map((scope) => {
        // Default: require approval (true) if no explicit pref stored
        const checked = prefs[scope] ?? true;
        return (
          <label
            key={scope}
            className="flex cursor-pointer items-center justify-between gap-3"
          >
            <span className="text-[12px] leading-[16px] text-foreground">
              {humanLabel(scope)}
            </span>
            <Switch
              checked={checked}
              onCheckedChange={(v) => handleToggle(scope, Boolean(v))}
              // Override the Switch primitive's purple `bg-primary` when on —
              // keep the rest of the Personal tab free of the primary accent.
              className="data-[state=checked]:bg-foreground"
            />
          </label>
        );
      })}
      <p className="text-[11px] leading-[14px] text-muted-foreground">
        When on, ORA will show a preview card and wait for your approval
        before doing anything on your behalf. When off, it will do it
        directly.
      </p>
    </div>
  );
}
